import { useReducer, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LoadingSpinner } from '@fff-web/fff-ui-shared';
import Layout from '../../components/Layout';
import { postContactUsForm } from '../../services/mailist';
import * as styles from './ContactUs.module.scss';

const SUBMIT_CTA = 'SUBMIT FORM';

const formReducer = (state, event) => {
  if (event.reset) {
    return {
      firstName: '',
      lastName: '',
      email: '',
      contactReason: '',
      description: '',
    };
  }
  return {
    ...state,
    [event.name]: event.value,
  };
};

const ContactUs = () => {
  const [formData, setFormData] = useReducer(formReducer, {});
  const [isLoading, setIsLoading] = useState(false);
  const { chatbotAppliedLab: showChatbot } = useFlags();

  const addFriendlyNameFormatToEmail = () => {
    const { firstName, lastName, email } = formData;
    return `${firstName} ${lastName} <${email}>`;
  };

  const handleChange = (event) => {
    setFormData({
      name: event.target.name,
      value: event.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const friendlyFormatFormData = {
      ...formData,
      email: addFriendlyNameFormatToEmail(),
    };

    setIsLoading(true);
    postContactUsForm(friendlyFormatFormData)
      .then(() => {
        setFormData({
          reset: true,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <Layout
      pageTitle="FabFitFun - Contact Us"
      meta={{
        metadata: [
          {
            name: 'description',
            content: 'Contact Us',
          },
        ],
      }}
      hideChatWidget={!showChatbot}
    >
      <section className={styles.cuContainer}>
        <h1 className={styles.cuTitle}>Contact Us</h1>
        <p className={styles.cuSubtitle} data-testid="contact-us-subtitle">
          Questions? Send us a text at <a href="sms:+13104094711">310-409-4711</a> [standard message
          rates do apply] or chat with us by clicking the &apos;Support&apos; option at the bottom
          right of the screen.
          <br />
          Our SMS and chat agents are available from 4:00 a.m. PT until 10 p.m. PT. If you send us a
          text while our agents are unavailable, we will respond to your message as soon as
          we&apos;re back online!
          <br />
          If you have a non-urgent question, connect with us via email! Just fill in the form below
          with your details.
        </p>
        <form className={styles.cuForm} onSubmit={handleSubmit} data-testid="contact-us-form">
          <div className={styles.cuNameWrapper}>
            <label className={styles.cuLabel} htmlFor="firstName">
              First Name*
              <input
                className={styles.cuInput}
                name="firstName"
                id="firstName"
                type="text"
                required
                onChange={handleChange}
                value={formData.firstName || ''}
              />
            </label>
            <label className={styles.cuLabel} htmlFor="lastName">
              Last Name*
              <input
                className={styles.cuInput}
                name="lastName"
                id="lastName"
                type="text"
                required
                onChange={handleChange}
                value={formData.lastName || ''}
              />
            </label>
          </div>
          <label className={styles.cuLabel} htmlFor="email">
            Email*
            <input
              className={styles.cuInput}
              name="email"
              id="email"
              type="email"
              required
              onChange={handleChange}
              value={formData.email || ''}
            />
          </label>
          <label className={styles.cuLabel} htmlFor="contactReason">
            Contact Reason*
            <span className={styles.cuContactReasonDetails}>
              Please select one from the following contact reason.
            </span>
            <select
              className={styles.cuSelect}
              id="contactReason"
              name="contactReason"
              required
              onChange={handleChange}
              value={formData.contactReason || ''}
            >
              <option hidden disabled value="">
                {' '}
                -- select an option --{' '}
              </option>
              <option value="Account Management">Account Management</option>
              <option value="Order Issue">Order Issue</option>
              <option value="Order Status">Order Status</option>
              <option value="Technical Issue">Technical Issue</option>
              <option value="Membership Information">Membership Information</option>
              <option value="Product Information">Product Information</option>
              <option value="Cancellation">Cancellation</option>
              <option value="Other">Other</option>
            </select>
          </label>
          <label className={styles.cuLabel} htmlFor="description">
            Description*
            <span className={styles.cuDescriptionDetails}>
              Please enter the details of your request. A member of our support staff will respond
              as soon as possible.
            </span>
            <textarea
              className={styles.cuTextarea}
              name="description"
              id="description"
              rows="10"
              cols="40"
              required
              onChange={handleChange}
              value={formData.description || ''}
              maxLength={600}
            />
          </label>
          <button className={styles.cuSubmitButton} disabled={isLoading} type="submit">
            {SUBMIT_CTA}
          </button>
        </form>
      </section>
    </Layout>
  );
};

export default ContactUs;
